import React from 'react';
import { Box, SxProps, Theme } from '@mui/material';
import { T } from '../../theme/tokens';

type CardVariant = 'paper' | 'glass' | 'elevated' | 'tonal' | 'dark';

interface SynCardProps {
    variant?:  CardVariant;
    accent?:   string;
    hover?:    boolean;
    sx?:       SxProps<Theme>;
    children:  React.ReactNode;
    onClick?:  () => void;
    padding?:  number | string;
}

const variantStyles: Record<CardVariant, object> = {
    paper: {
        background: T.color.surface.white,
        border: `1px solid ${T.color.border.light}`,
        boxShadow: T.shadow.card,
    },
    elevated: {
        background: T.color.surface.white,
        border: `1px solid ${T.color.border.light}`,
        boxShadow: T.shadow.cardHover,
    },
    glass: {
        background: T.color.glass.bgMid,
        border: `1px solid ${T.color.glass.border}`,
        boxShadow: T.shadow.sm,
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
    },
    tonal: {
        background: `rgba(13,61,79,0.04)`,
        border: `1px solid ${T.color.border.light}`,
        boxShadow: 'none',
    },
    dark: {
        background: `linear-gradient(135deg, ${T.color.teal[800]} 0%, ${T.color.teal[700]} 100%)`,
        border: `1px solid ${T.color.glass.border}`,
        boxShadow: T.shadow.md,
    },
};

export function SynCard({
    variant = 'paper',
    accent,
    hover = false,
    sx,
    children,
    onClick,
    padding = 3,
}: SynCardProps) {
    return (
        <Box
            onClick={onClick}
            sx={{
                borderRadius: `${T.radius.lg}px`,
                p: padding,
                position: 'relative',
                overflow: 'hidden',
                cursor: onClick ? 'pointer' : 'default',
                transition: 'box-shadow 0.18s ease, transform 0.18s ease',
                ...(hover && {
                    '&:hover': {
                        boxShadow: T.shadow.cardHover,
                        transform: 'translateY(-2px)',
                    },
                }),
                ...(accent && {
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0, top: 0, bottom: 0,
                        width: 4,
                        background: accent,
                        borderRadius: `${T.radius.lg}px 0 0 ${T.radius.lg}px`,
                    },
                    pl: `calc(${typeof padding === 'number' ? padding * 8 : padding}px + 12px)`,
                }),
                ...variantStyles[variant],
                ...(sx as object),
            }}
        >
            {children}
        </Box>
    );
}
